import { EntityBase } from '@provision/entity-types';
import { EntityNames } from 'src/graphql/types';

export type BulkCopyContext = {
  /** build a full URL for a given entity identifier (friendlyId, addressId, etc.) */
  getEntityUrl: (entityId: string) => string;
};

export type BulkCopyActionDefinition<T extends EntityBase = EntityBase> = {
  id: string;
  label: string;
  icon: string;
  /** extract the copyable string value from a single record */
  resolver: (record: T, context: BulkCopyContext) => string;
  isFriendlyLink?: boolean;
};

export type BulkCopyEntity = Extract<
  EntityNames,
  EntityNames.Conversation | EntityNames.Site | EntityNames.Client | EntityNames.Project
>;

/** safely read a property from a record that may have extra fields beyond EntityBase. */
const field = (record: EntityBase, key: string): unknown =>
  (record as unknown as Record<string, unknown>)[key];

const CONVERSATION_COPY_ACTIONS: BulkCopyActionDefinition[] = [
  {
    id: 'copyConversationId',
    label: 'Copy Conversation IDs',
    icon: 'content_copy',
    resolver: (record) => String(field(record, 'friendlyId') ?? record.id),
  },
  {
    id: 'copyConversationUrl',
    label: 'Copy Conversation URL',
    icon: 'link',
    resolver: (record, ctx) =>
      ctx.getEntityUrl(String(field(record, 'friendlyId') ?? record.id)),
  },
  {
    id: 'copyFriendlyIdAsLink',
    label: 'Copy Friendly ID as link',
    icon: 'content_copy',
    isFriendlyLink: true,
    resolver: (record, ctx) => {
      const friendlyId = String(field(record, 'friendlyId') ?? record.id);
      const url = ctx.getEntityUrl(friendlyId);
      return `[${friendlyId}](${url})`;
    },
  },
  {
    id: 'copyUuid',
    label: 'Copy UUID',
    icon: 'content_copy',
    resolver: (record) => String(field(record, 'conversationId') ?? record.id),
  },
];

const SITE_COPY_ACTIONS: BulkCopyActionDefinition[] = [
  {
    id: 'copySiteId',
    label: 'Copy Site ID',
    icon: 'content_copy',
    resolver: (record) => String(field(record, 'addressId') ?? record.id),
  },
  {
    id: 'copySiteUrl',
    label: 'Copy Site URL',
    icon: 'link',
    resolver: (record, ctx) =>
      ctx.getEntityUrl(String(field(record, 'addressId') ?? record.id)),
  },
];

const CLIENT_COPY_ACTIONS: BulkCopyActionDefinition[] = [
  {
    id: 'copyClientId',
    label: 'Copy Client ID',
    icon: 'content_copy',
    resolver: (record) => String(field(record, 'clientId') ?? record.id),
  },
  {
    id: 'copyClientUrl',
    label: 'Copy Client URL',
    icon: 'link',
    resolver: (record, ctx) =>
      ctx.getEntityUrl(String(field(record, 'clientId') ?? record.id)),
  },
];

const PROJECT_COPY_ACTIONS: BulkCopyActionDefinition[] = [
  {
    id: 'copyProjectId',
    label: 'Copy Project ID',
    icon: 'content_copy',
    resolver: (record) => String(record.id),
  },
  {
    id: 'copyProjectUrl',
    label: 'Copy Project URL',
    icon: 'link',
    resolver: (record, ctx) => ctx.getEntityUrl(String(record.id)),
  },
];

const BULK_COPY_ACTIONS: Record<BulkCopyEntity, BulkCopyActionDefinition[]> = {
  [EntityNames.Conversation]: CONVERSATION_COPY_ACTIONS,
  [EntityNames.Site]: SITE_COPY_ACTIONS,
  [EntityNames.Client]: CLIENT_COPY_ACTIONS,
  [EntityNames.Project]: PROJECT_COPY_ACTIONS,
};

export const getBulkCopyActions = (entity: BulkCopyEntity): BulkCopyActionDefinition[] => {
  return BULK_COPY_ACTIONS[entity] ?? [];
};

export const formatBulkCopyValues = (values: string[]): string => {
  return values.join('\n');
};

/**
 * joins friendly-link markdown strings as newline-separated text, with an
 * HTML variant that converts markdown links to anchors for rich-paste.
 */
export const formatBulkCopyLinks = (
  markdownLinks: string[],
): { plainText: string; html: string } => {
  const plainText = markdownLinks.join('\n');
  const htmlParts = markdownLinks.map((md) => {
    const match = /\[(?<label>[^\]]+)\]\((?<url>[^)]+)\)/.exec(md);
    if (match?.groups) {
      return `<a href="${match.groups.url}">${match.groups.label}</a>`;
    }
    return md;
  });
  const html = htmlParts.join('<br>');
  return { plainText, html };
};
